import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAuthEffect from "../hook/useAuthEffect";
import { fetchTask, fetchSuggestions } from "../redux/ducks/task";
import useEffectWithoutLoad from "../hook/useEffectWithoutLoad";

const TaskQARoute = ({ match }) => {
  const { taskID, rowIDq } = match.params;
  const dispatch = useDispatch();
  //const task = useSelector((state) => state.task.tasks[taskID]);
  const [qaResults, setQaResults] = useState({});
  const [loadingRows, setLoadingRows] = useState({}); // per-row loading state
  //const actualTaskID = rowIDq; // taskID is actually rowIDq
  //const actualRowID = taskID; // rowID is actually taskID
  const [isReady, setIsReady] = useState(false);
  const alreadyCheckedWords = useRef([]);
  const alreadyCheckedErrors = useRef([]);
  const task = useSelector((state) => state.task.tasks[rowIDq]);

  const suggestions = useSelector(
    (state) => state.task.tasks[rowIDq]?.suggestions,
  );

  useAuthEffect(() => {
    // load the task under the correct key
    setIsReady(false);

    if (!task?.rows) dispatch(fetchTask({ taskID: rowIDq }));

    // load suggestions for the correct row, and store them on the correct task
    if (rowIDq && taskID) {
      dispatch(fetchSuggestions({ taskID: rowIDq, rowID: taskID }));
    }
  }, [rowIDq, taskID, task?.rows]);

  useEffect(() => {
    if (suggestions) {
      // check whatever data you need
      setIsReady(true);
    }
    console.log("Glossary suggestions updated", suggestions);
  }, [suggestions]);

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function countOccurrences(
    haystack,
    needle,
    { wholeWord = true, caseInsensitive = true } = {},
  ) {
    if (!haystack || !needle) return 0;

    const raw = needle.trim();
    if (!raw) return 0;

    const flags = `g${caseInsensitive ? "i" : ""}`;

    // If it's a multi-word phrase, allow any whitespace between words
    if (/\s/.test(raw)) {
      const parts = raw.split(/\s+/).map(escapeRegExp);
      const pattern = parts.join("\\s+");
      const re = new RegExp(pattern, flags);
      return (haystack.match(re) || []).length;
    }

    // Single word
    const word = escapeRegExp(raw);
    const pattern = wholeWord ? `\\b${word}\\b` : word;
    const re = new RegExp(pattern, flags);
    return (haystack.match(re) || []).length;
  }

  const spellCheck = async (rowSource, rowData, languageCode, rowID) => {
    const TAG_REGEX = /<[^<>]*?>|\{[^{}]*\}|\[[^\[\]]*\]/g;

    const stripTagsForSpellcheck = (text) =>
      (text || "").replace(TAG_REGEX, " ").replace(/\s+/g, " ").trim();
    const isSpellWord = (w) => /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(w);
    //const words = rowData.split(" ");
    const spellText = stripTagsForSpellcheck(rowData);
    const words = spellText.split(" ").filter(Boolean).filter(isSpellWord);
    const uncheckedWords = words.filter(
      (word) => !alreadyCheckedWords.current.includes(word),
    );
    try {
      const response = await fetch(`${window.API_URL}/task/spellcheck.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: uncheckedWords, language: languageCode }),
      });

      const data = await response.json();

      const original = data.original || [];
      const corrected = data.corrected || [];

      console.log(
        "Already checked errors (skipped):",
        alreadyCheckedWords.current,
      );
      console.log("Words to check (not yet flagged):", uncheckedWords);

      const resultRows = [];

      corrected.forEach((word, index) => {
        if (word !== original[index]) {
          resultRows.push({
            type: "correction",
            original: original[index],
            corrected: word,
          });
        }
      });

      alreadyCheckedWords.current.forEach((word, index) => {
        resultRows.push({
          type: "correction",
          original: word,
          corrected: alreadyCheckedErrors.current[index],
        });
      });

      for (const term of suggestions.glossary || []) {
        const srcPhrase = term?.[0];
        const tgtPhrase = term?.[1];

        if (!srcPhrase || !tgtPhrase) continue;

        const srcCount = countOccurrences(rowSource, srcPhrase, {
          wholeWord: true,
        });
        if (srcCount === 0) continue; // phrase not in source, nothing to enforce

        const tgtCount = countOccurrences(rowData, tgtPhrase, {
          wholeWord: true,
        });
        console.log("rowData for glossary check", rowData);
        if (tgtCount < srcCount) {
          const missing = srcCount - tgtCount;
          resultRows.push({
            type: "warning",
            message:
              missing === 1
                ? `Glossary mismatch: "${srcPhrase}" appears ${srcCount}× in source but "${tgtPhrase}" appears ${tgtCount}× in target (missing 1)`
                : `Glossary mismatch: "${srcPhrase}" appears ${srcCount}× in source but "${tgtPhrase}" appears ${tgtCount}× in target (missing ${missing})`,
          });
          console.log("resultRows", resultRows);
        }
      }

      // Extract full numbers + ordinals (1st/2nd/3rd/13th), including simple decimals (3.14 / 3,14)
      const extractNumberTokens = (text) => {
        const clean = stripTagsForSpellcheck(text);

        // 1) ordinals first, so "1st" isn't partially treated as "1"
        // 2) numbers (optionally with . or , decimals)
        const re = /\b\d+(?:st|nd|rd|th)\b|\b\d+(?:[.,]\d+)*\b/gi;

        return (clean.match(re) || []).map((t) => t.toLowerCase());
      };

      // Multiset diff: returns what A has that B doesn't (by token count)
      const diffTokens = (aTokens, bTokens) => {
        const count = (arr) => {
          const m = new Map();
          for (const t of arr) m.set(t, (m.get(t) || 0) + 1);
          return m;
        };

        const a = count(aTokens);
        const b = count(bTokens);

        const diff = [];
        for (const [token, aCount] of a.entries()) {
          const bCount = b.get(token) || 0;
          const missingCount = aCount - bCount;
          if (missingCount > 0) {
            diff.push(
              missingCount === 1 ? token : `${token} (x${missingCount})`,
            );
          }
        }
        return diff;
      };

      // --- Numbers / ordinals consistency check (source vs target) ---
      const sourceTokens = extractNumberTokens(rowSource);
      const targetTokens = extractNumberTokens(rowData);

      // Source numbers missing in Target
      const missingInTarget = diffTokens(sourceTokens, targetTokens);
      if (missingInTarget.length > 0) {
        resultRows.push({
          type: "warning",
          message: `Missing numbers in target: ${missingInTarget.join(", ")}`,
        });
      }

      // Target has numbers that Source doesn't (extra numbers)
      const extraInTarget = diffTokens(targetTokens, sourceTokens);
      if (extraInTarget.length > 0) {
        resultRows.push({
          type: "warning",
          message: `Extra numbers in target (not in source): ${extraInTarget.join(
            ", ",
          )}`,
        });
      }

      if (!rowData || rowData.length === 0) {
        console.log("Number of rows below", rowData.length);
        resultRows.push({
          type: "warning",
          message: "Empty target",
        });
      }

      const correctedText = rowData;

      // --- TAG CHECK ( <...>  {...}  [...] ) ---

      const getTagCounts = (text) => {
        const matches = text.match(TAG_REGEX) || [];
        const counts = new Map();
        for (const m of matches) counts.set(m, (counts.get(m) || 0) + 1);
        return counts;
      };

      // use correctedText (your current behavior) or rowData if you prefer the raw target
      const sourceTags = getTagCounts(rowSource);
      const targetTags = getTagCounts(correctedText);

      // Missing tags (source -> target)
      for (const [tag, srcCount] of sourceTags.entries()) {
        const tgtCount = targetTags.get(tag) || 0;
        if (tgtCount < srcCount) {
          resultRows.push({
            type: "warning",
            message:
              srcCount - tgtCount === 1
                ? `Missing tag: ${tag}`
                : `Missing tag: ${tag} (x${srcCount - tgtCount})`,
          });
        }
      }

      // Extra tags (target -> source)
      for (const [tag, tgtCount] of targetTags.entries()) {
        const srcCount = sourceTags.get(tag) || 0;
        if (tgtCount > srcCount) {
          resultRows.push({
            type: "warning",
            message:
              tgtCount - srcCount === 1
                ? `Extra tag: ${tag}`
                : `Extra tag: ${tag} (x${tgtCount - srcCount})`,
          });
        }
      }
      // --- END TAG CHECK ---

      if (/^\s|\s$/.test(correctedText)) {
        resultRows.push({
          type: "warning",
          message: "Leading or trailing space",
        });
      }

      const repeatedWordsMatch = correctedText.match(/\b(\w+)(?:\s+\1\b)+/gi);
      if (repeatedWordsMatch) {
        repeatedWordsMatch.forEach((m) => {
          const word = m.split(" ")[0];
          resultRows.push({
            type: "warning",
            message: `Repeated word: work "${word}"`,
          });
        });
      }

      const doubleSpaceRegex = /(\S+)(\s{2,})(\S+)/g;
      let m;
      while ((m = doubleSpaceRegex.exec(rowData || "")) !== null) {
        resultRows.push({
          type: "warning",
          message: `Double space between:"${m[1]}" "${m[3]}"`,
        });
      }
      console.log("QA results", resultRows);
      // Save all results (server handles storing them)
      await fetch(`${window.API_URL}/task/saveQaResults.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          taskID: rowIDq,
          rowID: rowID,
          lang: languageCode,
          results: resultRows,
        }),
      });
    } catch (error) {
      console.error("Spellcheck failed:", error);
    }
  };

  const runSpellcheck = async (rowID, lang) => {
    setLoadingRows((prev) => ({ ...prev, [rowID]: true }));
    try {
      const res = await fetch(
        `${window.API_URL}/task/getQArow.php?rowID=${rowID}`,
      );
      const rows = await res.json(); // [{ id, source, target }]
      console.log("Fetched row for spellcheck", rows);
      const row = Array.isArray(rows) ? rows[0] : rows;

      if (!row) {
        console.warn("No row returned for rowID", rowID);

        return;
      }

      await spellCheck(row.source || "", row.target || "", lang, rowID);
      await fetchQaResults(); // refresh after running
    } catch (err) {
      console.error("Failed to run spellcheck for row", rowID, err);
    } finally {
      // hide spinner for this row
      setLoadingRows((prev) => ({ ...prev, [rowID]: false }));
    }
  };

  useAuthEffect(() => {
    if (!task?.rows) dispatch(fetchTask({ taskID }));
  }, []);

  useEffect(() => {
    if (taskID) fetchQaResults();
  }, [taskID]);

  const fetchQaResults = async () => {
    try {
      const res = await fetch(
        `${window.API_URL}/task/getQaResults.php?taskID=${taskID}`,
      );

      const data = await res.json();

      const grouped = (data || []).reduce((acc, row) => {
        if (!acc[row.qa_row_id]) {
          acc[row.qa_row_id] = {
            lang: row.qa_lang || "",
            // store qa_lang from first occurrence
            issues: [],
          };
        }
        acc[row.qa_row_id].issues.push(row);
        return acc;
      }, {});

      alreadyCheckedWords.current = [];
      alreadyCheckedErrors.current = [];

      Object.values(grouped).forEach(({ issues }) => {
        issues.forEach((issue) => {
          if (
            issue.qa_context?.startsWith("Spelling Error:") &&
            issue.qa_status !== "i"
          ) {
            // Extracts "clavesir" from "Spelling Error: clavesir. -> clave sir."
            const match = issue.qa_context.match(
              /Spelling Error:\s*(.+?)\s*->\s*(.+)/,
            );
            if (match) {
              alreadyCheckedWords.current.push(match[1].trim());
              alreadyCheckedErrors.current.push(match[2].trim());
            }
          }
        });
      });
      console.log("Fetched QA results", grouped);
      setQaResults(grouped);
    } catch (err) {
      console.error("Failed to load QA results", err);
    }
  };

  const handleIgnoreToggle = async (qaID, rowID, isIgnored) => {
    const action = isIgnored ? "unignore" : "ignore";
    try {
      await fetch(`${window.API_URL}/task/saveQaResults.php?${action}=${qaID}`);

      setQaResults((prev) => {
        const current = prev[rowID];
        if (!current) return prev;

        const updatedIssues = current.issues.map((row) =>
          row.qa_id === qaID
            ? { ...row, qa_status: isIgnored ? "a" : "i" }
            : row,
        );
        console.log("Save QA results", updatedIssues);
        return {
          ...prev,
          [rowID]: { ...current, issues: updatedIssues }, // preserve lang
        };
      });
    } catch (err) {
      console.error(`Failed to ${action} QA row`, err);
    }
  };

  return (
    <div style={{ padding: "2rem", color: "#c8c8c8", fontSize: "14px" }}>
      <h2>QA Results</h2>

      {Object.keys(qaResults).length === 0 ? (
        <p>No QA issues found.</p>
      ) : (
        Object.entries(qaResults).map(([rowID, { lang, issues }]) => {
          const isLoading = !!loadingRows[rowID];
          const hasIssues = Array.isArray(issues) && issues.length > 0;

          return (
            <div key={rowID} style={{ marginBottom: "2rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: "0.5rem",
                }}
              >
                <button
                  onClick={() => runSpellcheck(rowID, lang)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                    cursor: isLoading
                      ? "wait"
                      : hasIssues
                        ? "pointer"
                        : "not-allowed",
                    background: "transparent",
                    color: "inherit",
                    opacity: isLoading || !hasIssues ? 0.6 : 1,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  aria-busy={isLoading}
                  disabled={isLoading || !hasIssues || !isReady} // disable when loading OR when nothing found for this row
                  title={
                    !hasIssues
                      ? "No QA issues for this row"
                      : isLoading
                        ? "Running QA checks…"
                        : "Run QA checks"
                  }
                >
                  {isLoading ? (
                    <>
                      <span
                        className="spinner"
                        style={{
                          margin: "0px",
                        }}
                      />
                    </>
                  ) : (
                    "Run QA checks"
                  )}
                </button>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #ccc" }}>
                    <th style={{ textAlign: "left", padding: "8px" }}>
                      Suggestion
                    </th>
                    <th style={{ textAlign: "center", padding: "8px" }}>
                      Ignore
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((row) => (
                    <tr
                      key={row.qa_id}
                      style={{
                        transition: "background 0.2s",
                        textDecoration:
                          row.qa_status === "i" ? "line-through" : "none",
                        opacity: row.qa_status === "i" ? 0.5 : 1,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f0f4ff";
                        e.currentTarget.style.color = "black";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "inherit";
                      }}
                    >
                      <td style={{ padding: "8px" }}>{row.qa_context}</td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={row.qa_status === "i"}
                          onChange={() =>
                            handleIgnoreToggle(
                              row.qa_id,
                              row.qa_row_id,
                              row.qa_status === "i",
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
};

export default TaskQARoute;
